import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {Form, Modal, Spinner} from 'react-bootstrap'
import {useAppDispatch} from 'src/redux/storeHooks'
import {
  getEmailTemplatesAsync,
  sendBulkCustomerEmailAsync,
} from 'src/redux/actions/emailTemplateActions'
import {
  BulkEmailFilters,
  BulkEmailListFilters,
  EmailTemplate,
} from 'src/interfaces/EmailTemplate.interface'
import {EMAIL_TEMPLATE_TYPES} from './email-templates/EmailTemplateForm'
import useAppToast from 'src/hooks/useAppToast'
import Loader from 'src/utils/Loader'
import './send-customer-email.scss'

const FROM_EMAIL_OPTIONS = [
  {
    value: 'Help@mycommercium.com',
    label: 'Help@mycommercium.com (Marketing & Sales)',
  },
  {
    value: 'support@mycommercium.com',
    label: 'support@mycommercium.com (Customer Support)',
  },
] as const

type FromEmailValue = (typeof FROM_EMAIL_OPTIONS)[number]['value']

const formatTypeLabel = (type: string) => {
  const match = EMAIL_TEMPLATE_TYPES.find((item) => item.value === type)
  return match?.label ?? type.replace(/_/g, ' ')
}

const isTemplateActive = (template: EmailTemplate) => {
  const value = template.is_active as unknown
  return value === true || value === 1 || value === '1' || value === 'true'
}

const parseTemplateList = (payload: unknown): EmailTemplate[] => {
  if (!payload || typeof payload !== 'object') return []

  const body = payload as Record<string, unknown>
  const statusCode = Number(body.status_code)

  if (!Number.isNaN(statusCode) && statusCode >= 400) {
    return []
  }

  const data = body.data ?? body
  if (Array.isArray(data)) {
    return data as EmailTemplate[]
  }

  if (data && typeof data === 'object') {
    const nested = data as Record<string, unknown>
    if (Array.isArray(nested.results)) {
      return nested.results as EmailTemplate[]
    }
    if (Array.isArray(nested.items)) {
      return nested.items as EmailTemplate[]
    }
  }

  return []
}

const isSuccessResponse = (payload: unknown) => {
  if (!payload || typeof payload !== 'object') return false
  const body = payload as Record<string, unknown>
  const statusCode = Number(body.status_code)

  if (!Number.isNaN(statusCode)) {
    return statusCode >= 200 && statusCode < 300
  }

  return true
}

export type SendEmailRecipient = {
  id: number
  name: string
  email: string
}

const buildBulkEmailFilters = (
  userIds: number[],
  listFilters?: BulkEmailListFilters
): BulkEmailFilters => {
  const filters: BulkEmailFilters = {
    user_ids: userIds,
  }

  if (listFilters?.is_active === 'true') {
    filters.is_active = true
  } else if (listFilters?.is_active === 'false') {
    filters.is_active = false
  }

  if (listFilters?.billing_plan_id) {
    const billingPlanId = Number(listFilters.billing_plan_id)
    if (!Number.isNaN(billingPlanId)) {
      filters.billing_plan_id = billingPlanId
    }
  }

  return filters
}

type Props = {
  show: boolean
  recipients: SendEmailRecipient[]
  customerFilters?: BulkEmailListFilters
  onClose: () => void
  onSent?: () => void
}

const SendCustomerEmailModal: FC<Props> = ({
  show,
  recipients,
  customerFilters,
  onClose,
  onSent,
}) => {
  const customerIds = useMemo(() => recipients.map((recipient) => recipient.id), [recipients])
  const recipientCount = recipients.length
  const dispatch = useAppDispatch()
  const {successToast, errorToast} = useAppToast()
  const errorToastRef = useRef(errorToast)

  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [selectedFromEmail, setSelectedFromEmail] = useState<FromEmailValue>(
    FROM_EMAIL_OPTIONS[0].value
  )
  const [sending, setSending] = useState(false)

  useEffect(() => {
    errorToastRef.current = errorToast
  }, [errorToast])

  useEffect(() => {
    if (!show) return

    let cancelled = false
    setSelectedTemplateId('')
    setSelectedFromEmail(FROM_EMAIL_OPTIONS[0].value)
    setLoadFailed(false)
    setLoadingTemplates(true)

    dispatch(getEmailTemplatesAsync({}))
      .then((response) => {
        if (cancelled) return

        if (getEmailTemplatesAsync.rejected.match(response)) {
          setLoadFailed(true)
          setTemplates([])
          errorToastRef.current('Failed to load email templates.')
          return
        }

        const parsed = parseTemplateList(response.payload)
        setTemplates(parsed)
        setLoadFailed(parsed.length === 0)
      })
      .catch(() => {
        if (cancelled) return
        setLoadFailed(true)
        setTemplates([])
        errorToastRef.current('Failed to load email templates.')
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTemplates(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [dispatch, show])

  const selectableTemplates = useMemo(() => {
    const active = templates.filter(isTemplateActive)
    return active.length > 0 ? active : templates
  }, [templates])

  const selectedTemplate = useMemo(
    () => selectableTemplates.find((template) => String(template.id) === selectedTemplateId),
    [selectableTemplates, selectedTemplateId]
  )

  const handleSend = () => {
    if (!selectedTemplate) {
      errorToast('Please select a message template.')
      return
    }

    if (!customerIds.length) {
      errorToast('Select at least one customer to send email.')
      return
    }

    if (!selectedFromEmail) {
      errorToast('Please select a from email address.')
      return
    }

    setSending(true)
    dispatch(
      sendBulkCustomerEmailAsync({
        subject: selectedTemplate.name,
        template_id: selectedTemplate.id,
        body: selectedTemplate.template_data,
        from_email: selectedFromEmail,
        filters: buildBulkEmailFilters(customerIds, customerFilters),
      })
    )
      .then((response) => {
        if (isSuccessResponse(response.payload)) {
          successToast('Email sent successfully.')
          onSent?.()
          onClose()
          return
        }
        const payload = response.payload as {massage?: string; message?: string}
        errorToast(payload?.massage || payload?.message || 'Failed to send email.')
      })
      .catch(() => errorToast('Failed to send email.'))
      .finally(() => setSending(false))
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size='xl'
      className='bulk-email-engine-modal'
      dialogClassName='bulk-email-engine-modal__dialog'
    >
      <Modal.Header closeButton className='bulk-email-engine-modal__header'>
        <div className='d-flex align-items-center gap-3 w-100 pe-5'>
          <Modal.Title className='fw-bold fs-3 text-dark mb-0'>Bulk Email Engine</Modal.Title>
          <span
            className='bulk-email-engine-modal__count-badge'
            aria-label={`${recipientCount} target customer${recipientCount === 1 ? '' : 's'} selected`}
          >
            {recipientCount} Target Customer{recipientCount === 1 ? '' : 's'} Selected
          </span>
        </div>
      </Modal.Header>

      <Modal.Body className='bulk-email-engine-modal__body'>
        {loadingTemplates ? (
          <div className='bulk-email-engine-modal__loading d-flex justify-content-center align-items-center'>
            <Loader variant="section" />
          </div>
        ) : (
          <div className='row g-4 g-xl-5 align-items-stretch bulk-email-engine-modal__grid h-100'>
            <div className='col-12 d-flex flex-column bulk-email-engine-modal__config-col'>
              <p className='bulk-email-engine-modal__step-title fw-bold text-dark mb-4'>
                1. Select Template
              </p>

              <Form.Select
                className='bulk-email-engine-modal__select mb-5'
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                disabled={selectableTemplates.length === 0}
              >
                <option value=''>Choose a template...</option>
                {selectableTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {formatTypeLabel(template.type)}
                    {!isTemplateActive(template) ? ' (Inactive)' : ''}
                  </option>
                ))}
              </Form.Select>

              {loadFailed || selectableTemplates.length === 0 ? (
                <p className='text-danger fs-8 mb-4'>
                  No email templates available. Create an active template under Email Templates first.
                </p>
              ) : null}

              <Form.Label className='fw-bold text-dark mb-2'>From Email address</Form.Label>
              <Form.Select
                className='bulk-email-engine-modal__select mb-5'
                value={selectedFromEmail}
                onChange={(e) => setSelectedFromEmail(e.target.value as FromEmailValue)}
              >
                {FROM_EMAIL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <Form.Label className='fw-bold text-dark mb-2'>Subject Line</Form.Label>
              <Form.Control
                className='bulk-email-engine-modal__subject-input'
                type='text'
                readOnly
                value={selectedTemplate?.name ?? ''}
                placeholder='Select a template to load subject line'
              />
            </div>

            <div className='col-12 d-flex flex-column bulk-email-engine-modal__preview-col'>
              <p className='bulk-email-engine-modal__step-title fw-bold text-dark mb-4'>
                2. Payload Preview
              </p>

              {selectedTemplate ? (
                <div
                  className='bulk-email-engine-modal__preview flex-grow-1'
                  dangerouslySetInnerHTML={{__html: selectedTemplate.template_data}}
                />
              ) : (
                <div className='bulk-email-engine-modal__preview bulk-email-engine-modal__preview--empty flex-grow-1'>
                  Select a template to preview the email content.
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className='bulk-email-engine-modal__footer'>
        <button
          type='button'
          className='btn btn-light fw-semibold px-5'
          onClick={onClose}
          disabled={sending}
        >
          Cancel Process
        </button>
        <button
          type='button'
          className='btn btn-primary fw-bold px-5'
          onClick={handleSend}
          disabled={sending || !selectedTemplateId || !selectedFromEmail || customerIds.length === 0}
        >
          {sending ? 'Sending...' : 'Bulk Send'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default SendCustomerEmailModal
